import type { ProjectViewState } from '@freedom/shared';
import type { ProjectObjectRecord, ProjectWorkspaceObject } from './api';
import type { DisplaySettings } from './radio-workspace';

export const WORKSPACE_DRAFT_VERSION = 1;
const WORKSPACE_DRAFT_PREFIX = 'freedom-isp-design:workspace-draft:v1:';
const MAX_DRAFT_CHARS = 5_000_000;
const MAX_DRAFT_OBJECTS = 5_000;

export interface DraftCoverageConfig {
  maxCoverageRadiusM: number;
  azimuthDeg: number;
  mechanicalTiltDeg: number;
  electricalTiltDeg: number;
  horizontalBeamwidthDeg: number;
  verticalBeamwidthDeg: number;
}

export interface WorkspaceDraftPayload {
  projectId: string | null;
  projectName: string;
  objects: ProjectWorkspaceObject[];
  viewState: ProjectViewState;
  displaySettings: DisplaySettings;
  coverageConfig: DraftCoverageConfig;
  selectedRadioLinkId: string | null;
  rainRateMmPerHour: number;
  dirty: boolean;
}

export interface WorkspaceDraft extends WorkspaceDraftPayload {
  version: typeof WORKSPACE_DRAFT_VERSION;
  userId: string;
  updatedAt: number;
}

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

function validObject(value: unknown): value is ProjectWorkspaceObject {
  if (!isRecord(value) || typeof value.objectType !== 'string' || typeof value.name !== 'string') return false;
  return isRecord(value.geometry) && isRecord(value.properties);
}

export function workspaceDraftKey(userId: string): string {
  return `${WORKSPACE_DRAFT_PREFIX}${encodeURIComponent(userId)}`;
}

export function saveWorkspaceDraft(storage: DraftStorage, userId: string, payload: WorkspaceDraftPayload, now = Date.now()): boolean {
  if (!userId || payload.objects.length > MAX_DRAFT_OBJECTS || !Number.isFinite(now) || Number.isNaN(new Date(now).getTime())) return false;
  try {
    const draft: WorkspaceDraft = { ...payload, version: WORKSPACE_DRAFT_VERSION, userId, updatedAt: now };
    const text = JSON.stringify(draft);
    if (text.length > MAX_DRAFT_CHARS) return false;
    storage.setItem(workspaceDraftKey(userId), text);
    return true;
  } catch {
    return false;
  }
}

export function loadWorkspaceDraft(storage: DraftStorage, userId: string): WorkspaceDraft | null {
  if (!userId) return null;
  try {
    const text = storage.getItem(workspaceDraftKey(userId));
    if (!text || text.length > MAX_DRAFT_CHARS) return null;
    const value: unknown = JSON.parse(text);
    if (!isRecord(value) || value.version !== WORKSPACE_DRAFT_VERSION || value.userId !== userId || typeof value.updatedAt !== 'number' || !Number.isFinite(value.updatedAt)) return null;
    if (Number.isNaN(new Date(value.updatedAt).getTime())) return null;
    if (!Array.isArray(value.objects) || value.objects.length > MAX_DRAFT_OBJECTS || !value.objects.every(validObject)) return null;
    if (!isRecord(value.viewState) || !isRecord(value.displaySettings) || !isRecord(value.coverageConfig)) return null;
    if (value.projectId !== null && typeof value.projectId !== 'string') return null;
    if (typeof value.projectName !== 'string' || value.projectName.length > 300) return null;
    if (value.selectedRadioLinkId !== null && typeof value.selectedRadioLinkId !== 'string') return null;
    if (typeof value.rainRateMmPerHour !== 'number' || !Number.isFinite(value.rainRateMmPerHour) || typeof value.dirty !== 'boolean') return null;
    return value as unknown as WorkspaceDraft;
  } catch {
    return null;
  }
}

export function removeWorkspaceDraft(storage: DraftStorage, userId: string): void {
  try { storage.removeItem(workspaceDraftKey(userId)); } catch { /* Browser storage can be disabled. */ }
}

export function draftProjectRecords(draft: WorkspaceDraft): ProjectObjectRecord[] {
  const updatedAt = new Date(draft.updatedAt).toISOString();
  const objects = draft.objects.map((object, index): ProjectObjectRecord => ({ ...object, id: `draft-${index}`, updatedAt }));
  objects.push({
    id: 'draft-workspace-settings',
    objectType: 'workspace_settings',
    name: 'Настройки временного черновика',
    geometry: { type: 'GeometryCollection', geometries: [] },
    properties: { workspaceSettings: draft.displaySettings },
    updatedAt,
  });
  return objects;
}
